import { DeleteAction } from '@hendrx/icons/DeleteAction';
import { Reset } from '@hendrx/icons/Reset';
import { ColorInput as MantineColorInput } from '@mantine/core';
import { mergeRefs } from '@mantine/hooks';
import cx from 'clsx';
import { forwardRef, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';

import classes from './ColorInput.module.scss';
import { changeColorOpacity } from './utils/changeColorOpacity';
import { getDisplayColor } from './utils/getDisplayColor';
import { ColorInputSize, ColorSwatchSize } from '../../../types/ColorPicker';
import { hexToRgba } from '../../../utils/hexToRgba';
import { rem } from '../../../utils/rem';
import { ActionIconButton } from '../../buttons/ActionIconButton';
import { ColorSwatch } from '../../pickers/ColorSwatch';
import { PercentageInput } from '../PercentageInput';

type ColorInputProps = {
  color: string;
  onSelectColor: (color: string) => void;
  selected?: boolean;
  size?: ColorInputSize;
  onClick?: () => void;
  withOpacity?: boolean;
  onDelete?: () => void;
  onReset?: () => void;
};

export const ColorInput = forwardRef<HTMLInputElement, ColorInputProps>((props, ref) => {
  const {
    color,
    onSelectColor,
    selected = false,
    size = ColorInputSize.large,
    withOpacity = false,
    onClick,
    onDelete,
    onReset
  } = props;
  const [localColor, setLocalColor] = useState(color || '');

  const displayColor = useMemo(() => {
    return getDisplayColor(localColor);
  }, [localColor]);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const mergedRef = mergeRefs(ref, inputRef);

  useEffect(() => {
    setLocalColor(color || '');
  }, [color]);

  const opacity = useMemo(() => {
    const { a } = hexToRgba(localColor) ?? { a: 0 };
    return Math.round(a * 100);
  }, [localColor]);

  const onChangeOpactity = (newOpacity: string | number) => {
    let opacity = Number(newOpacity);
    if (isNaN(opacity)) {
      opacity = 0;
    }
    const newColor = changeColorOpacity(color, Number(newOpacity));
    if (newColor) {
      setLocalColor(newColor);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      inputRef.current?.blur();
    }
  };

  const handleColorChange = () => {
    onSelectColor(localColor);
  };

  return (
    <MantineColorInput
      format="hexa"
      ref={mergedRef}
      withEyeDropper={false}
      withPreview={false}
      withPicker={false}
      value={displayColor}
      onChange={setLocalColor}
      onBlur={handleColorChange}
      onKeyDown={onKeyDown}
      leftSection={<ColorSwatch size={ColorSwatchSize.small} color={localColor} />}
      leftSectionPointerEvents={onClick ? 'all' : 'none'}
      leftSectionProps={onClick && { onClick }}
      leftSectionWidth={rem(30)}
      data-testid="color-input"
      classNames={{
        root: cx(classes.inputRoot, { [classes.small]: size === ColorInputSize.small }),
        input: classes.input,
        wrapper: cx(classes.inputWrapper, { [classes.selected]: selected }),
        section: withOpacity ? classes.opacitySection : onDelete || onReset ? classes.actionButtonSection : undefined
      }}
      rightSection={
        (withOpacity || onDelete || onReset) && (
          <>
            {withOpacity && (
              <PercentageInput
                classNames={{ wrapper: classes.opacityInputWrapper, input: classes.opacityInput }}
                value={opacity}
                onChange={onChangeOpactity}
                onBlur={handleColorChange}
              />
            )}
            {onDelete ? (
              <ActionIconButton onClick={onDelete} variant="transparent" Icon={<DeleteAction />} />
            ) : onReset ? (
              <ActionIconButton
                onClick={onReset}
                variant="transparent"
                dataTestId="reset-color-button"
                Icon={<Reset />}
              />
            ) : null}
          </>
        )
      }
      rightSectionWidth={rem(48)}
    />
  );
});
